/**
 * Admin Tier Testing Service
 * Allows testing all subscription tiers without actual payment
 * Works in both dev and production
 */

export type TestTier = 'free' | 'starter' | 'pro' | 'business' | 'enterprise';

const STORAGE_KEY = 'admin_tier_override';
const ADMIN_ENABLED_KEY = 'admin_testing_enabled';

export class AdminTierService {
  /**
   * Check if admin testing mode is enabled
   */
  static isAdminMode(): boolean {
    return localStorage.getItem(ADMIN_ENABLED_KEY) === 'true';
  }

  /**
   * Enable admin testing mode
   * @param secretCode Optional secret code for security (can add password protection)
   */
  static enableAdminMode(secretCode?: string): boolean {
    // Optional: Add password protection
    // const ADMIN_CODE = import.meta.env.VITE_ADMIN_CODE || 'test123';
    // if (secretCode !== ADMIN_CODE) return false;
    
    localStorage.setItem(ADMIN_ENABLED_KEY, 'true');
    console.log('🔓 Admin testing mode ENABLED');
    return true;
  }

  /**
   * Disable admin testing mode and clear override
   */
  static disableAdminMode(): void {
    localStorage.removeItem(ADMIN_ENABLED_KEY);
    localStorage.removeItem(STORAGE_KEY);
    console.log('🔒 Admin testing mode DISABLED');
  }

  /**
   * Get the tier override (if any)
   */
  static getOverrideTier(): TestTier | null {
    if (!this.isAdminMode()) return null;
    
    const override = localStorage.getItem(STORAGE_KEY);
    return override as TestTier | null;
  }

  /**
   * Set tier override
   */
  static setOverrideTier(tier: TestTier): void {
    if (!this.isAdminMode()) {
      console.warn('⚠️ Admin mode not enabled. Call enableAdminMode() first.');
      return;
    }
    
    localStorage.setItem(STORAGE_KEY, tier);
    console.log(`✅ Tier override set to: ${tier}`);
    
    // Trigger page reload to apply changes
    window.dispatchEvent(new Event('admin-tier-changed'));
  }

  /**
   * Clear tier override (use actual subscription)
   */
  static clearOverride(): void {
    localStorage.removeItem(STORAGE_KEY);
    console.log('✅ Tier override cleared - using actual subscription');
    window.dispatchEvent(new Event('admin-tier-changed'));
  }

  /**
   * Get available test tiers
   */
  static getAvailableTiers(): { value: TestTier; label: string; description: string }[] {
    return [
      {
        value: 'free',
        label: 'Free Tier',
        description: '1 saved, 1 export/month, with branding'
      },
      {
        value: 'starter',
        label: 'Starter ($4.99/mo)',
        description: '3 saved, 10 exports/mo, profile image'
      },
      {
        value: 'pro',
        label: 'Pro ($9.99/mo)',
        description: '10 saved, unlimited exports, all addons'
      },
      {
        value: 'business',
        label: 'Business ($19.99/mo)',
        description: 'Unlimited, team features, campaigns'
      },
      {
        value: 'enterprise',
        label: 'Enterprise (Custom)',
        description: 'API access, dedicated support'
      }
    ];
  }

  /**
   * Quick console commands for testing
   */
  static setupConsoleCommands(): void {
    if (typeof window !== 'undefined') {
      (window as any).adminTier = {
        enable: () => this.enableAdminMode(),
        disable: () => this.disableAdminMode(),
        setFree: () => this.setOverrideTier('free'),
        setStarter: () => this.setOverrideTier('starter'),
        setPro: () => this.setOverrideTier('pro'),
        setBusiness: () => this.setOverrideTier('business'),
        setEnterprise: () => this.setOverrideTier('enterprise'),
        clear: () => this.clearOverride(),
        current: () => this.getOverrideTier(),
        help: () => {
          console.log(`
🎯 Admin Tier Testing Commands:
--------------------------------
adminTier.setFree()     - Test as Free tier
adminTier.setStarter()  - Test as Starter tier ($4.99)
adminTier.setPro()      - Test as Pro tier ($9.99)
adminTier.setBusiness() - Test as Business tier ($19.99)
adminTier.setEnterprise() - Test as Enterprise
adminTier.clear()       - Clear override (use real tier)
adminTier.current()     - Show current override
adminTier.help()        - Show this help

🔐 Admin Panel Access:
----------------------
• Visit: ${window.location.origin}/admin
• Username: Set via VITE_ADMIN_USERNAME (default: admin)
• Password: Set via VITE_ADMIN_PASSWORD (default: admin123)
• Available in both DEV and PRODUCTION
• Console commands also available for quick testing

Example:
> adminTier.setFree()
> // Test free tier features
> adminTier.setPro()
> // Test pro tier features
> adminTier.clear()
          `);
        }
      };

      console.log('💡 Admin tier testing available! Type adminTier.help() for commands');
    }
  }
}

// Auto-setup console commands in development
if (import.meta.env.DEV) {
  AdminTierService.setupConsoleCommands();
}

